import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { useTourGuideHover } from "./useTourGuideHover";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { ROUTE_NAMES } from "@root/constants";

export const useProjectDocumentChatbotTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const {
    setHighlightedItemKey,
    setActiveTourModule,
    setCurrentTourStep,
    hoverTooltipsEnabled,
  } = useTourGuideContext();

  // Tour guide refs
  const projectSelectRef = useRef<HTMLDivElement>(null);

  const isProjectChatbot =
    pathname === ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT ||
    pathname.includes("/document-chat/");

  // Tour guide configuration for project document chatbot
  const projectDocumentChatbotTourConfig: TourGuideConfig = useMemo(() => {
    if (!isProjectChatbot) {
      return {
        moduleId: "projectDocumentChatbot",
        steps: [],
        enabled: false,
      };
    }

    return {
      moduleId: "projectDocumentChatbot",
      enabled: true,
      steps: [
        {
          id: "sidebar-project",
          targetSelector: '[data-tour-item="projectDocumentChatbot"]',
          title:
            t("tourGuide.projectDocumentChatbot.step1.title") ||
            "Project Document Chatbot",
          description:
            t("tourGuide.projectDocumentChatbot.step1.description") ||
            "You're now in the Project view! This section helps you analyze project documents, drawings, and files. Let's explore the features.",
          position: "right" as const,
          offset: { x: 20, y: 0 },
        },
        {
          id: "project-select",
          targetRef: projectSelectRef,
          title:
            t("tourGuide.projectDocumentChatbot.step2.title") ||
            "Select Your Project",
          description:
            t("tourGuide.projectDocumentChatbot.step2.description") ||
            "Choose the project you want to work with. This dropdown shows all your available projects.",
          position: "left" as const,
          offset: { x: -20, y: 0 },
        },
        {
          id: "project-chatbot-dropdown",
          targetSelector: '[data-tour-item="project-chatbot-dropdown"]',
          title:
            t("tourGuide.projectDocumentChatbot.step3.title") ||
            "Select Files or switch chatbot",
          description:
            t("tourGuide.projectDocumentChatbot.step3.description") ||
            "Select your files by clicking on + icon or switch to drawing or multi-document analysis chatbot.",
          position: "top" as const,
          offset: { x: 0, y: -12 },
        },
      ],
    };
  }, [isProjectChatbot, t]);

  const baseTourGuide = useTourGuide(projectDocumentChatbotTourConfig);
  
  // Auto-start tour guide and handle completion tracking
  const projectDocumentChatbotTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourCompletionFlag: "project_tour_completed",
    isOnTourPath: isProjectChatbot,
  });

  // Sync tour guide with context
  useEffect(() => {
    if (projectDocumentChatbotTourGuide.isActive) {
      setActiveTourModule("projectDocumentChatbot");
      setCurrentTourStep(projectDocumentChatbotTourGuide.currentStep);
      if (projectDocumentChatbotTourGuide.currentStep === 0) {
        setHighlightedItemKey("projectDocumentChatbot");
      } else {
        setHighlightedItemKey(null);
      }
    } else {
      setActiveTourModule(null);
      setCurrentTourStep(0);
      setHighlightedItemKey(null);
    }
  }, [
    projectDocumentChatbotTourGuide.isActive,
    projectDocumentChatbotTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
    setHighlightedItemKey,
  ]);

  // Hover tooltips for project document chatbot tour guide
  const projectSelectStep = projectDocumentChatbotTourConfig.steps.find(
    (step) => step.id === "project-select"
  );
  const projectChatbotDropdownStep =
    projectDocumentChatbotTourConfig.steps.find(
      (step) => step.id === "project-chatbot-dropdown"
    );

  const projectSelectHover = useTourGuideHover(
    "project-select",
    projectSelectStep || null
  );
  const projectChatbotDropdownHover = useTourGuideHover(
    "project-chatbot-dropdown",
    projectChatbotDropdownStep || null
  );

  // Attach hover handlers to project chatbot dropdown via selector
  useEffect(() => {
    if (
      !hoverTooltipsEnabled ||
      projectDocumentChatbotTourGuide.isActive ||
      !projectChatbotDropdownStep
    )
      return;

    const element = document.querySelector(
      '[data-tour-item="project-chatbot-dropdown"]'
    ) as HTMLElement;
    if (element) {
      const handleMouseEnter = () => {
        if (!projectChatbotDropdownHover.tooltipData) return;
        projectChatbotDropdownHover.hoverProps.onMouseEnter();
      };
      const handleMouseLeave = () => {
        projectChatbotDropdownHover.hoverProps.onMouseLeave();
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [
    hoverTooltipsEnabled,
    projectDocumentChatbotTourGuide.isActive,
    projectChatbotDropdownStep,
    projectChatbotDropdownHover.hoverProps,
    projectChatbotDropdownHover.tooltipData,
  ]);

  return {
    projectSelectRef,
    projectDocumentChatbotTourGuide,
    projectDocumentChatbotTourConfig,
    isProjectChatbot,
    projectSelectHover,
    projectChatbotDropdownHover,
  };
};
